import React, { useEffect } from 'react';
import {
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
  required,
  useGetList,
  useRecordContext,
} from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Divider,
  Grid,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { PermissionLevel } from '../generated/data-contracts';

interface RolePermissionManagerProps {
  /**
   * El source field para el array de roles en el formulario
   * @default 'roles'
   */
  source?: string;
  /**
   * Título personalizado para la sección
   * @default 'Roles y Permisos'
   */
  title?: string;
  /**
   * Subtítulo personalizado para la sección
   * @default 'Asignación de roles y niveles de permisos'
   */
  subtitle?: string;
  /**
   * Texto de ayuda adicional
   */
  helperText?: string;
  /**
   * Si debe mostrar el card contenedor
   * @default true
   */
  showCard?: boolean;
  /**
   * Configuración de validación requerida
   * @default true
   */
  isRequired?: boolean;
}


const RolePermissionManager: React.FC<RolePermissionManagerProps> = ({
  source = 'roles',
  title = 'Roles y Permisos',
  subtitle = 'Asignación de roles y niveles de permisos',
  helperText,
  showCard = true,
  isRequired = true,
}) => {
  const record = useRecordContext();

  // Transformar rolePermissions a roles si es necesario
  useEffect(() => {
    if (record && record.rolePermissions && !record.roles) {
      console.log(
        '🔄 RolePermissionManager - Transformando rolePermissions a roles para el formulario'
      );
      console.log('🔄 rolePermissions originales:', record.rolePermissions);

      // Crear el array de roles transformado
      const transformedRoles = record.rolePermissions.map((rp: any) => ({
        role: rp.role?.toUpperCase() || '',
        permissionLevel: rp.permissionLevel,
      }));

      console.log('🔄 roles transformados:', transformedRoles);

      // Actualizar el record con los roles transformados
      record.roles = transformedRoles;
    }
  }, [record]);

  // Obtener roles disponibles para la selección
  const { data: roles, isLoading: rolesLoading } = useGetList('roles', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const roleChoices = React.useMemo(() => {
    if (!roles || rolesLoading) return [];
    return roles.map((role: any) => ({
      id: (role.name || role.roleName || '').toUpperCase(),
      name: role.name || role.roleName || '',
    }));
  }, [roles, rolesLoading]);

  // Opciones de permisos - simplificadas para evitar referencias circulares
  const permissionChoices = [
    {
      id: PermissionLevel.READ,
      name: 'Lectura',
    },
    {
      id: PermissionLevel.WRITE,
      name: 'Escritura',
    },
    {
      id: PermissionLevel.ADMIN,
      name: 'Administrador',
    },
  ];

  const arrayInputContent = (
    <ArrayInput source={source} label='Asignaciones de Roles y Permisos'>
<SimpleFormIterator
  disableAdd={false}
  disableRemove={false}
  sx={{
    '& .RaSimpleFormIterator-form': {
      backgroundColor: 'background.paper',
      padding: '16px 12px', // ← Menos padding horizontal, mismo vertical
      borderRadius: 2,
      marginBottom: 2,
      border: '1px solid #e0e0e0',
      width: '100%',
    },
    '& .RaSimpleFormIterator-line': {
      width: '100%',
      minWidth: '100%',
    },
  }}
>
  <Box sx={{ width: '100%', padding: '0 8px' }}> {/* ← Padding interno reducido */}
    <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
      <Grid item xs={12} sm={6} sx={{ minWidth: 0, flex: 1, padding: '0 4px' }}> {/* ← Padding lateral reducido */}
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <SelectInput
            source='role'
            label='Rol'
            choices={roleChoices}
            validate={isRequired ? required('Seleccione un rol') : undefined}
            fullWidth
            optionText={choice => choice.name}
            optionValue='id'
            sx={{ 
              width: '100%',
              '& .MuiFormControl-root': { width: '100%' },
              '& .MuiInputBase-root': { width: '100%' }
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} sx={{ minWidth: 0, flex: 1, padding: '0 4px' }}> {/* ← Mismo padding lateral */}
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <SelectInput
            source='permissionLevel'
            label='Nivel de Permiso'
            choices={permissionChoices}
            validate={
              isRequired
                ? required('Seleccione un nivel de permiso')
                : undefined
            }
            fullWidth
            optionText='name'
            optionValue='id'
            sx={{ 
              width: '100%',
              '& .MuiFormControl-root': { width: '100%' },
              '& .MuiInputBase-root': { width: '100%' }
            }}
          />
        </Box>
      </Grid>
    </Grid>
  </Box>
</SimpleFormIterator>
    </ArrayInput>
  );

  if (!showCard) {
    return (
      <Box>
        {arrayInputContent}
        {helperText && (
          <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3, width: '100%' }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: '#FF9800' }}>
            <SecurityIcon />
          </Avatar>
        }
        title={title}
        subheader={subtitle}
      />
      <CardContent>
        {arrayInputContent}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 2 }}>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            <strong>Niveles de Permisos:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <PersonIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
              <Typography variant='caption' sx={{ fontWeight: 500 }}>
                Lectura
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <SecurityIcon sx={{ fontSize: 16, color: '#FF9800' }} />
              <Typography variant='caption' sx={{ fontWeight: 500 }}>
                Escritura
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <AdminIcon sx={{ fontSize: 16, color: '#F44336' }} />
              <Typography variant='caption' sx={{ fontWeight: 500 }}>
                Administrador
              </Typography>
            </Box>
          </Box>
        </Box>

        {helperText && (
          <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
            {helperText}
          </Typography>
        )}

        <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
          Los roles determinan qué usuarios pueden acceder a esta entidad,
          mientras que los niveles de permisos definen qué acciones pueden
          realizar.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default RolePermissionManager;